import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export function DigestPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/me/digest-prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({ frequency: "weekly", delivery: "both" }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/digest-prefs", prefs);
      setPrefs(data);
      toast.success("Digest preferences saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.put("/me/digest-prefs", prefs);
      const { data } = await api.post("/me/digest-test");
      const bits = [];
      if (data?.sent?.email) bits.push(`email to ${data.email}`);
      if (data?.sent?.in_app) bits.push("in-app notification");
      toast.success(bits.length ? `Test digest sent (${bits.join(" + ")})` : "Test digest generated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  if (!prefs) return null;
  return (
    <Card className="p-5 shadow-none mt-4" data-testid="digest-prefs">
      <div className="flex items-center gap-2 mb-1"><Mail size={16} className="text-orange-500" /><h3 className="font-display font-semibold">Activity Digest</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Choose how often you receive the platform activity summary (new users, logins, storage) and how it is delivered.</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Frequency</label>
          <Select value={prefs.frequency} onValueChange={(v) => setPrefs({ ...prefs, frequency: v })}>
            <SelectTrigger className="mt-1" data-testid="digest-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly (Mondays)</SelectItem>
              <SelectItem value="off">Off</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Delivery</label>
          <Select value={prefs.delivery} onValueChange={(v) => setPrefs({ ...prefs, delivery: v })}>
            <SelectTrigger className="mt-1" data-testid="digest-delivery"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Email + in-app</SelectItem>
              <SelectItem value="email">Email only</SelectItem>
              <SelectItem value="in_app">In-app only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} data-testid="digest-save">{saving ? "Saving…" : "Save preferences"}</Button>
        <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="digest-test">{testing ? "Sending…" : "Send me a test digest now"}</Button>
      </div>
    </Card>
  );
}
